import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { KeyRound } from 'lucide-react';
import api from '../../api/client';
import { normalizeUnit, kmToUnit, speedUnitLabel } from '../../utils/units';
import OdometerDisplay from './OdometerDisplay';
import TodayTrail from './TodayTrail';

// Fix default marker icons (CRA asset issue)
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const motoIcon = L.divIcon({
  html: `<div style="
    width:36px;height:36px;background:#e63946;border-radius:50% 50% 50% 0;
    transform:rotate(-45deg);border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.5)
  "></div>`,
  iconSize: [36, 36],
  iconAnchor: [18, 36],
  className: '',
});

function FlyTo({ lat, lng }: { lat: number; lng: number }) {
  const map = useMap();
  useEffect(() => { map.flyTo([lat, lng], 15, { duration: 1.2 }); }, [lat, lng, map]);
  return null;
}

interface Position {
  device_id: string;
  latitude: number;
  longitude: number;
  speed?: number;
  timestamp: string;
  attributes?: { power?: number; battery?: number; ignition?: boolean; sat?: number; [key: string]: unknown };
}

function powerColor(v: number): string {
  if (v > 13.0) return '#4caf50';
  if (v >= 12.0) return '#ffc107';
  return '#e63946';
}

function satColor(s: number): string {
  if (s >= 4) return '#4caf50';
  if (s >= 2) return '#ffc107';
  return '#e63946';
}

interface Geofence {
  id: string;
  center_lat: number;
  center_lon: number;
  radius_m: number;
}

interface Props {
  deviceId: string;
  deviceName: string;
  devicePlate?: string | null;
  preferredUnit?: string;
}

export default function LiveMap({ deviceId, deviceName, devicePlate, preferredUnit }: Props) {
  const [position, setPosition] = useState<Position | null>(null);
  const [error, setError] = useState('');
  const [geofence, setGeofence] = useState<Geofence | null>(null);
  const [odometerKm, setOdometerKm] = useState<number | null>(null);
  const unit = normalizeUnit(preferredUnit);

  const fetchPosition = async () => {
    try {
      const { data } = await api.get(`/devices/${deviceId}/last-position`);
      if (data) setPosition(data);
    } catch {
      setError('Brak danych GPS');
    }
  };

  useEffect(() => {
    fetchPosition();
    const interval = setInterval(fetchPosition, 30000);
    return () => clearInterval(interval);
  }, [deviceId]);

  useEffect(() => {
    api.get(`/geofences/${deviceId}`)
      .then((r) => setGeofence(r.data.length > 0 ? r.data[0] : null))
      .catch(() => {});
  }, [deviceId]);

  useEffect(() => {
    const fetchOdometer = () => {
      api.get(`/devices/${deviceId}/odometer`)
        .then((r) => setOdometerKm(r.data.total_distance_km))
        .catch(() => setOdometerKm(null));
    };
    fetchOdometer();
    const interval = setInterval(fetchOdometer, 30000);
    return () => clearInterval(interval);
  }, [deviceId]);

  if (error && !position) {
    return (
      <div className="map-placeholder">
        <p>{error}</p>
        <p className="map-hint">Urządzenie wyśle pozycję gdy zostanie uruchomione</p>
      </div>
    );
  }

  const defaultCenter: [number, number] = position
    ? [position.latitude, position.longitude]
    : [52.2297, 21.0122];

  return (
    <div className="live-map-wrapper">
      <div className="map-container">
        <MapContainer
          center={defaultCenter}
          zoom={position ? 15 : 6}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          <TodayTrail deviceId={deviceId} />
          {geofence && (
            <Circle
              center={[geofence.center_lat, geofence.center_lon]}
              radius={geofence.radius_m}
              pathOptions={{ color: '#e63946', fillColor: '#e63946', fillOpacity: 0.08, weight: 1.5, dashArray: '6 4' }}
            />
          )}
          {position && position.latitude !== 0 && (
            <>
              <FlyTo lat={position.latitude} lng={position.longitude} />
              <Marker position={[position.latitude, position.longitude]} icon={motoIcon}>
                <Popup>
                  <strong>{deviceName}</strong>
                  {devicePlate && <><br />{devicePlate}</>}
                  <br />
                  {new Date(position.timestamp + 'Z').toLocaleString()}
                </Popup>
              </Marker>
            </>
          )}
        </MapContainer>
      </div>
      {position && (
        <div className="map-status-bar">
          <div className="map-status-line map-status-indicators">
            {position.attributes?.ignition != null && (
              <span
                style={{ color: position.attributes.ignition ? '#ffc107' : '#666666', display: 'inline-flex', alignItems: 'center' }}
                title={position.attributes.ignition ? 'Zapłon włączony' : 'Zapłon wyłączony'}
              >
                <KeyRound size={16} />
              </span>
            )}
            {position.speed != null && (() => {
              const kmh = Math.round(position.speed * 1.852);
              const displaySpeed = Math.round(kmToUnit(kmh, unit));
              return (
                <span
                  className="speed-display"
                  style={{ color: kmh > 0 ? '#ffffff' : '#555' }}
                >
                  {String(displaySpeed).padStart(3, '0')} {speedUnitLabel(unit)}
                </span>
              );
            })()}
            {position.attributes?.power != null && (
              <span style={{ color: powerColor(position.attributes.power) }}>
                ⚡ {position.attributes.power.toFixed(2)}V
              </span>
            )}
            {position.attributes?.battery != null && position.attributes.battery > 0 && (
              <span style={{ color: '#aaa' }}>
                🔋 {position.attributes.battery.toFixed(2)}V
              </span>
            )}
            {position.attributes?.sat != null && position.attributes.sat > 0 && (
              <span style={{ color: satColor(position.attributes.sat) }}>
                🛰️ {position.attributes.sat}
              </span>
            )}
            {odometerKm != null && <OdometerDisplay km={odometerKm} unit={unit} />}
          </div>
          <div className="map-status-line">
            Ostatnia aktualizacja: {new Date(position.timestamp + 'Z').toLocaleTimeString()}
          </div>
        </div>
      )}
    </div>
  );
}
